import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { Advantage, DigitalCourse, DigitalCourseFilter, DigitalCourseQueryParams } from '../../models';
import { Question } from '../../models/question';
import { BaseApiService } from '@core/http/base.api.service';
import { ConfigService } from '@core/services/config.service';
import { PageList } from '@core/models/page-list';
import { Guid } from '@core/utils/guid';
import { Review } from '@shared/models/review';
import { Participant } from '@modules/course/classroom/models/participant';

@Injectable({
  providedIn: 'root'
})
export class DigitalCourseApiService extends BaseApiService {
  constructor(public http: HttpClient, private configService: ConfigService) {
    super(http);
  }

  get baseUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.course}/api/course`;
  }

  get userUrl(): string {
    const config = this.configService.getCurrent();
    return `${config.services.booking}/api/user`;
  }

  getAllDigitalCourse(
    queryParams: DigitalCourseQueryParams,
    filter = new DigitalCourseFilter()
  ):  Observable<PageList<DigitalCourse>> {
    const params = new HttpParams()
      .append('search', queryParams.name)
      .append('sort', queryParams.sort || '')
      .append('pageOffset', queryParams.pageOffset ? String(queryParams.pageOffset) : '0')
      .append('pageSize', queryParams.pageSize ? String(queryParams.pageSize) : '10')
      .append('categories', filter.categories)
      .append('onlyApproved', filter.onlyApprove ? 'true' : 'false')
      .append('onlyPublished', 'true')
      .append('from', filter.fromDate)
      .append('to', filter.toDate);

    return super.getAllWithPagination<DigitalCourse>(this.baseUrl,  params );
  }


  getDigitalCourse(id: string): Observable<DigitalCourse> {
    if (Guid.IsGuid(id)) {
      return this.http.get<DigitalCourse>(`${this.baseUrl}/${id}`).pipe(map((response) => response));
    } else {
      return this.http.get<DigitalCourse>(`${this.baseUrl}/${id}/slug`).pipe(map((response) => response));
    }
  }

  getDigitalCourseReviews(
    id: string,
    pageOffset: number = 0,
    pageSize: number = 3,
    sort: string = 'rating desc'
  ): Observable<PageList<Review>> {
    const params = new HttpParams()
      .append('pageOffset', pageOffset.toString())
      .append('pageSize', pageSize.toString())
      .append('sort', sort);

    return super.getAllWithPagination<Review>(`${this.baseUrl}/${id}/review`, params);
  }

  sendCourseReview(id: string, review: Review): Observable<Review> {
    return this.http.post<Review>(`${this.baseUrl}/${id}/review`, review);
  }

  getCourseAdvantage(courseId: string): Observable<Advantage[]> {
    return this.http.get<Advantage[]>(`${this.baseUrl}/${courseId}/getadvantages`);
  }

  getCourseFaq(courseId: string): Observable<Question[]> {
    return this.http.get<Question[]>(`${this.baseUrl}/${courseId}/questions`);
  }

  checkPartnerIdentity(phone: string): Observable<Participant> {
    return this.http.post<Participant>(`${this.userUrl}/getUserInfoByPhoneOrEmail`,{ phone });
  }

  isUserReview(userId: string, courseId: string): Observable<boolean> {
    return this.http.get<boolean>(`${this.baseUrl}/${courseId}/getreview?userId=${userId}`);
  }
}
